/**
 * The shape the article search needs. Deliberately not `ISelectOption`: the
 * article list comes straight off the GraphQL query, where both strings are
 * nullable, and every read below already tolerates that.
 */
export interface IProductSearchOption {
  id: string | number;
  label?: string | null;
  articleNo?: string | null;
}

interface ISearchKeys {
  articleNo: string;
  label: string;
  combined: string;
}

/** Lower-cased search keys per option object, built once per opened editor. */
export type TProductSearchIndex = Map<IProductSearchOption, ISearchKeys>;

/* One collator each, built once for the module.
`String.prototype.localeCompare` builds a fresh collator on every single call,
which made it by far the most expensive part of re-filtering the article list on
each keystroke (WA-3803). `Intl.Collator` with the same options is the same
comparison, done once. */
const articleNoCollator = new Intl.Collator(undefined, { numeric: false });
const labelCollator = new Intl.Collator(undefined, { numeric: true });

const searchKeysFor = (option: IProductSearchOption): ISearchKeys => {
  const articleNo = (option?.articleNo ?? '').toLowerCase();
  const label = (option?.label ?? '').toLowerCase();
  return { articleNo, label, combined: `${articleNo} ${label}` };
};

/**
 * Pre-compute the lower-cased keys the article search matches against.
 *
 * Build this once for an article list (the list does not change while a cell is
 * open) so a keystroke never re-derives them. Keyed by the option object rather
 * than by index, because MUI hands `filterOptions` a *filtered* array when
 * `filterSelectedOptions` is on, so positions do not line up.
 */
export const buildProductSearchIndex = (
  options: readonly (IProductSearchOption | null | undefined)[],
): TProductSearchIndex => {
  const index: TProductSearchIndex = new Map();
  for (const option of options) {
    if (option) index.set(option, searchKeysFor(option));
  }
  return index;
};

interface IRanked<T> {
  option: T;
  /** Original-case key the collator sorts on. */
  sortKey: string;
  /** Whether the article number starts with the query, not merely contains it. */
  starts: boolean;
}

/**
 * Filter and order the article list for the product cell's combobox.
 *
 * Matches on article number first, then on description, and sorts so that
 * article numbers that *start* with what was typed come first; the ordering is
 * intentionally replicated from Windows WaWi to make the transition easier for
 * users, and is unchanged from the inline implementation this replaces.
 *
 * What changed (WA-3803) is the cost. It used to run inside a comparator: two
 * `getBlock` calls per comparison, each lower-casing both the article number and
 * the description, then a `localeCompare`. On an article list of any real size
 * that is n·log n string allocations plus n·log n freshly-built collators per
 * keystroke — and the combobox input is controlled, so a keystroke arriving
 * while React is still rendering the previous one gets reverted, i.e. the digit
 * the user typed silently disappears. Here each option is classified once, in a
 * single pass, against pre-computed keys, and each bucket is sorted on one
 * shared collator. `Array.prototype.sort` is stable, so concatenating the two
 * sorted buckets is the same result the two-level comparator produced.
 */
export const filterProductOptions = <T extends IProductSearchOption>(
  options: readonly T[],
  index: TProductSearchIndex,
  inputValue: string,
): T[] => {
  const query = (inputValue ?? '').toLowerCase();
  const byArticleNo: IRanked<T>[] = [];
  const byLabel: IRanked<T>[] = [];

  for (const option of options) {
    if (!option) continue;
    // An option can be absent from the index only if the list changed under an
    // open editor; deriving its keys on the spot keeps that from dropping it.
    const keys = index.get(option) ?? searchKeysFor(option);
    if (keys.articleNo.includes(query)) {
      byArticleNo.push({
        option,
        sortKey: option.articleNo ?? '',
        starts: keys.articleNo.startsWith(query),
      });
    } else if (keys.label.includes(query)) {
      byLabel.push({ option, sortKey: option.label ?? '', starts: false });
    } else if (keys.combined.includes(query)) {
      // The query spans the gap between number and description ("100 brot").
      byArticleNo.push({
        option,
        sortKey: option.articleNo ?? '',
        starts: keys.articleNo.startsWith(query),
      });
    }
  }

  byArticleNo.sort((a, b) => {
    if (a.starts !== b.starts) return a.starts ? -1 : 1;
    return articleNoCollator.compare(a.sortKey, b.sortKey);
  });
  byLabel.sort((a, b) => labelCollator.compare(a.sortKey, b.sortKey));

  return byArticleNo.concat(byLabel).map(({ option }) => option);
};
